import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell,
} from "recharts";
import {
  Users, GraduationCap, Wallet, TrendingUp, Award, ShieldCheck, Activity, CalendarCheck, Banknote, Download, FileSpreadsheet, Ticket, Send, Loader2, Gift, AlertTriangle, MailCheck,
} from "lucide-react";

const downloadFile = async (path, filename) => {
  const token = localStorage.getItem("pf_token");
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) { toast.error("Export failed"); return; }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  toast.success("Download started");
};

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [users, setUsers] = useState([]);
  const [payouts, setPayouts] = useState([]);

  const load = () => {
    api.get("/admin/analytics").then((r) => setD(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const verify = async (id) => {
    await api.post(`/admin/counsellor/${id}/verify`);
    toast.success("Counsellor verified");
    load();
  };

  const approvePayout = async (id) => {
    await api.post(`/admin/payout/${id}/approve`);
    toast.success("Payout approved");
    load();
  };

  if (!d) return <div className="p-8 text-slate-400">Loading...</div>;

  const funnelColors = ["#06B6D4", "#6366F1", "#10B981", "#F59E0B"];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-8 py-8">
      <span className="font-mono-data text-xs uppercase tracking-[0.2em] text-cyan-400">Command Center</span>
      <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight">Platform Analytics</h1>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric icon={GraduationCap} label="Total Students" value={d.total_students} color="cyan" />
        <Metric icon={Users} label="Counsellors" value={d.total_counsellors} color="indigo" />
        <Metric icon={Wallet} label="Revenue" value={`₹${(d.revenue / 1000).toFixed(0)}K`} color="emerald" />
        <Metric icon={TrendingUp} label="Conversion" value={`${d.conversion_rate}%`} color="amber" />
      </div>
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric icon={Award} label="Paid Subscribers" value={d.paid_subscribers} color="amber" />
        <Metric icon={CalendarCheck} label="Total Bookings" value={d.total_bookings} color="cyan" />
        <Metric icon={Activity} label="Assessments" value={d.assessments_completed} color="emerald" />
        <Metric icon={ShieldCheck} label="AI Accuracy" value={`${d.ai_accuracy}%`} color="indigo" />
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
          <h3 className="font-display text-lg font-semibold mb-4">Conversion funnel</h3>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={d.funnel} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 12 }} />
                <YAxis type="category" dataKey="stage" tick={{ fill: "#94a3b8", fontSize: 12 }} width={80} />
                <Tooltip contentStyle={{ background: "#0F1626", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} cursor={{ fill: "rgba(6,182,212,0.05)" }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {d.funnel.map((_, i) => <Cell key={i} fill={funnelColors[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
          <h3 className="font-display text-lg font-semibold mb-4">Top counsellors</h3>
          <div className="space-y-3">
            {d.top_counsellors.map((c, i) => (
              <div key={c.name} className="flex items-center justify-between rounded-xl bg-white/[0.02] p-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono-data text-sm font-bold text-slate-500">#{i + 1}</span>
                  <div>
                    <p className="text-sm font-medium text-white">{c.name}</p>
                    <p className="text-xs text-slate-400">{c.sessions} sessions · ⭐ {c.rating}</p>
                  </div>
                </div>
                <span className="font-mono-data text-sm text-emerald-300">₹{(c.earnings / 1000).toFixed(0)}K</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pending verifications */}
      <div className="mt-6 rounded-2xl border border-white/8 bg-[#131C2E] p-6">
        <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-amber-400" /> Counsellor verification requests
        </h3>
        {d.pending_verifications.length === 0 ? (
          <p className="text-sm text-slate-400">No pending verifications.</p>
        ) : (
          <div className="space-y-3">
            {d.pending_verifications.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-xl bg-white/[0.02] p-4">
                <div>
                  <p className="text-sm font-medium text-white">{c.name}</p>
                  <p className="text-xs text-slate-400">{c.headline} · {c.experience_years}y exp</p>
                </div>
                <button data-testid="admin-approve-btn" onClick={() => verify(c.id)}
                  className="rounded-full bg-emerald-400 px-4 py-1.5 text-xs font-bold text-[#041016] transition-transform hover:scale-105">
                  Approve
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payout requests */}
      <div className="mt-6 rounded-2xl border border-white/8 bg-[#131C2E] p-6">
        <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <Banknote className="h-5 w-5 text-emerald-400" /> Counsellor payout requests
        </h3>
        {payouts.length === 0 ? (
          <p className="text-sm text-slate-400">No payout requests.</p>
        ) : (
          <div className="space-y-3">
            {payouts.map((p) => (
              <div key={p.id} data-testid="admin-payout-row" className="flex items-center justify-between rounded-xl bg-white/[0.02] p-4">
                <div>
                  <p className="text-sm font-medium text-white">{p.counsellor_name}</p>
                  <p className="text-xs text-slate-400">₹{p.amount.toLocaleString()} · {new Date(p.created_at).toLocaleDateString("en-IN")}</p>
                </div>
                {p.status === "pending" ? (
                  <button data-testid="admin-approve-payout-btn" onClick={() => approvePayout(p.id)}
                    className="rounded-full bg-emerald-400 px-4 py-1.5 text-xs font-bold text-[#041016] transition-transform hover:scale-105">
                    Approve
                  </button>
                ) : (
                  <span className="rounded-full bg-emerald-400/15 px-2.5 py-1 text-xs font-semibold text-emerald-300 capitalize">{p.status}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Users table */}
      <AiUsagePanel />
      <SnapshotPanel />
      <PaymentHealthPanel />
      <OrdersPanel />
      <CouponPanel />
      <CleanupPanel onDone={() => window.location.reload()} />
      <LoginHistoryPanel />
      <div className="mt-6 rounded-2xl border border-white/8 bg-[#131C2E] p-6 overflow-x-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-display text-lg font-semibold">All users ({users.length})</h3>
          <div className="flex gap-2">
            <button data-testid="export-students-csv-btn" onClick={() => downloadFile("/admin/students/export?format=csv", "students.csv")}
              className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-xs font-bold text-cyan-300 hover:bg-cyan-400/15">
              <Download className="h-4 w-4" /> Students CSV
            </button>
            <button data-testid="export-students-xlsx-btn" onClick={() => downloadFile("/admin/students/export?format=xlsx", "students.xlsx")}
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400 px-4 py-2 text-xs font-bold text-[#041016] hover:scale-[1.02] transition-transform">
              <FileSpreadsheet className="h-4 w-4" /> Students Excel
            </button>
          </div>
        </div>
        <table className="w-full" data-testid="admin-user-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="pb-3 font-medium">Name</th>
              <th className="pb-3 font-medium">Email</th>
              <th className="pb-3 font-medium">Role</th>
              <th className="pb-3 font-medium">Plan</th>
            </tr>
          </thead>
          <tbody>
            {users.slice(0, 30).map((u) => (
              <tr key={u.id} className="border-t border-white/5 text-sm">
                <td className="py-2.5 text-white">{u.name}</td>
                <td className="py-2.5 text-slate-400">{u.email}</td>
                <td className="py-2.5"><span className="rounded-full bg-white/5 px-2 py-0.5 text-xs capitalize text-slate-300">{u.role}</span></td>
                <td className="py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs capitalize ${u.plan === "free" ? "bg-slate-700/50 text-slate-300" : "bg-amber-400/15 text-amber-300"}`}>{u.plan}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AiUsagePanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/ai-usage").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <div data-testid="ai-usage-panel" className="mt-6 rounded-2xl border border-cyan-400/20 bg-[#131C2E] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Activity className="h-5 w-5 text-cyan-400" /> AI usage &amp; pilot spend</h3>
        <span className="rounded-full bg-white/5 px-3 py-1 text-[11px] text-slate-400">model: {d.model}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
        <div className="rounded-xl bg-white/[0.03] p-4"><p className="font-display text-2xl font-extrabold text-cyan-300" data-testid="ai-total-calls">{d.total_calls}</p><p className="text-xs text-slate-400">total AI calls</p></div>
        <div className="rounded-xl bg-white/[0.03] p-4"><p className="font-display text-2xl font-extrabold text-emerald-300" data-testid="ai-total-cost">${d.total_cost}</p><p className="text-xs text-slate-400">est. spend</p></div>
        <div className="rounded-xl bg-white/[0.03] p-4"><p className="font-display text-2xl font-extrabold text-amber-300">${(d.total_cost / Math.max(1, d.days.length)).toFixed(2)}</p><p className="text-xs text-slate-400">avg / active day</p></div>
      </div>
      <div className="space-y-1.5">
        {d.days.length === 0 && <p className="text-sm text-slate-500">No AI usage recorded yet.</p>}
        {d.days.map((x) => (
          <div key={x.day} className="flex items-center justify-between text-sm border-b border-white/5 py-1.5">
            <span className="text-slate-300 font-mono-data text-xs">{x.day}</span>
            <span className="text-slate-400">{x.calls} calls</span>
            <span className="text-emerald-300 font-mono-data text-xs">${x.cost}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-slate-500">Estimates use per-call token averages on the current model. Free chat is capped at 5 messages/user.</p>
    </div>
  );
}

function CleanupPanel({ onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/users/cleanup-preview"); setPreview(data); setConfirm(false); }
    catch { toast.error("Could not load preview"); } finally { setBusy(false); }
  };
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/cleanup");
      toast.success(`Removed ${data.deleted_users} test account(s). ${data.remaining_users} users remain.`);
      setPreview(null); setConfirm(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cleanup failed"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="cleanup-panel" className="mt-6 rounded-2xl border border-white/10 bg-[#131C2E] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold">Clean test accounts</h3>
          <p className="text-xs text-slate-400 mt-1">Removes every non-admin account whose email is <b>not</b> @gmail.com or @fuelwings.in (plus their orders, sessions and notifications). ak@entrancezone.com is always kept.</p>
        </div>
        {!preview && <button data-testid="cleanup-preview-btn" onClick={load} disabled={busy} className="rounded-full bg-white/10 px-4 py-2 text-xs font-bold text-white disabled:opacity-50">{busy ? "Loading…" : "Preview what will be removed"}</button>}
      </div>
      {preview && (
        <div className="mt-4" data-testid="cleanup-preview">
          <p className="text-sm text-slate-200"><span className="font-bold text-rose-300" data-testid="cleanup-count">{preview.to_delete}</span> of {preview.total_users} accounts will be removed · {preview.keep} kept</p>
          <div className="mt-2 max-h-40 overflow-y-auto rounded-xl bg-white/[0.03] p-3 text-[11px] font-mono-data text-slate-400 space-y-0.5">
            {preview.users.map((u) => <div key={u.email}>{u.email} <span className="text-slate-600">· {u.plan} · {u.created_at?.slice(0, 10)}</span></div>)}
            {preview.users.length === 0 && <div className="text-emerald-300">Nothing to clean — database is tidy.</div>}
          </div>
          {preview.to_delete > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {!confirm ? (
                <button data-testid="cleanup-arm-btn" onClick={() => setConfirm(true)} className="rounded-full border border-rose-400/40 px-4 py-2 text-xs font-bold text-rose-300 hover:bg-rose-400/10">Remove {preview.to_delete} account(s)…</button>
              ) : (
                <button data-testid="cleanup-confirm-btn" onClick={run} disabled={busy} className="rounded-full bg-rose-500 px-4 py-2 text-xs font-bold text-white disabled:opacity-50">{busy ? "Removing…" : `Yes, permanently delete ${preview.to_delete}`}</button>
              )}
              <button data-testid="cleanup-cancel-btn" onClick={() => { setPreview(null); setConfirm(false); }} className="rounded-full bg-white/5 px-4 py-2 text-xs text-slate-300">Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LoginHistoryPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/auth/admin-logins").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <div data-testid="login-history-panel" className="mt-6 rounded-2xl border border-white/10 bg-[#131C2E] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-cyan-400" /> Admin sign-ins</h3>
        <span className="rounded-full bg-white/5 px-3 py-1 text-[11px] text-slate-400" data-testid="known-devices">{d.known_devices} known device(s) · new devices trigger a WhatsApp alert</span>
      </div>
      {d.logins.length === 0 && <p className="text-sm text-slate-500">No sign-ins recorded yet.</p>}
      <div className="space-y-1.5">
        {d.logins.map((l, i) => (
          <div key={i} className="flex flex-wrap items-center justify-between gap-2 text-xs border-b border-white/5 py-1.5">
            <span className={`rounded-full px-2 py-0.5 ${l.new_device ? "bg-amber-400/10 text-amber-300" : "bg-emerald-400/10 text-emerald-300"}`}>{l.new_device ? "new device" : "known"}</span>
            <span className="text-slate-300">{l.device}</span>
            <span className="font-mono-data text-slate-400">{l.ip}</span>
            <span className="text-slate-500 flex-1 min-w-[160px] truncate">{l.location}</span>
            <span className="text-slate-500">{new Date(l.at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SnapshotPanel() {
  const [s, setS] = useState(null);
  const [offset, setOffset] = useState(1);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get(`/admin/daily-snapshot?day_offset=${offset}`).then((r) => setS(r.data)).catch(() => {}); }, [offset]);
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/daily-snapshot/send?day_offset=${offset}`);
      toast.success(`Snapshot for ${data.snapshot.date} sent to WhatsApp`);
    } catch { toast.error("Could not send snapshot"); } finally { setSending(false); }
  };
  if (!s) return null;
  const cell = (v, label, color = "text-white", tid) => (
    <div className="rounded-xl bg-white/[0.03] p-4"><p className={`font-display text-2xl font-extrabold ${color}`} data-testid={tid}>{v}</p><p className="text-xs text-slate-400">{label}</p></div>
  );
  return (
    <div data-testid="snapshot-panel" className="mt-6 rounded-2xl border border-amber-400/20 bg-[#131C2E] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><CalendarCheck className="h-5 w-5 text-amber-400" /> Daily snapshot <span className="font-mono-data text-xs text-slate-400" data-testid="snapshot-date">{s.date}</span></h3>
        <div className="flex items-center gap-2">
          <select data-testid="snapshot-day-select" value={offset} onChange={(e) => setOffset(Number(e.target.value))}
            className="rounded-full border border-white/10 bg-[#0B1420] px-3 py-1.5 text-xs text-slate-200 outline-none">
            <option value={0}>Today so far</option><option value={1}>Yesterday</option><option value={2}>2 days ago</option>
          </select>
          <button data-testid="send-snapshot-btn" onClick={sendNow} disabled={sending}
            className="flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1.5 text-xs font-bold text-[#041016] disabled:opacity-50">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send to WhatsApp now
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {cell(s.signups, "new students", "text-cyan-300", "snapshot-signups")}
        {cell(s.assessments_completed, "assessments done", "text-indigo-300")}
        {cell(`${s.payments}/${s.checkouts_started}`, `paid / checkouts (${s.conversion_rate}%)`, "text-white", "snapshot-payments")}
        {cell(`₹${s.revenue}`, "revenue", "text-emerald-300", "snapshot-revenue")}
        {cell(s.payment_failures, "payment failures", s.payment_failures ? "text-rose-300" : "text-white")}
        {cell(`${s.refunds} (₹${s.refund_amount})`, "refunds", "text-amber-300")}
      </div>
      <p className="mt-3 text-[11px] text-slate-500">Auto-sent to the admin WhatsApp every morning at 09:00 IST for the previous day (IST calendar day).</p>
    </div>
  );
}

function PaymentHealthPanel() {
  const [d, setD] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [showDns, setShowDns] = useState(false);
  useEffect(() => { api.get("/admin/payment-failures?limit=10").then((r) => setD(r.data)).catch(() => {}); }, []);
  const verifyDomain = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/admin/email-domain/verify");
      setD((p) => ({ ...p, email_domain_status: data.status, email_dns_records: data.records, email_sender: data.sender }));
      if (data.status === "verified") toast.success(`Domain verified — emails now sent from ${data.sender}`);
      else toast.info(`Domain status: ${data.status}. DNS can take up to an hour to propagate.`);
    } catch { toast.error("Could not check domain"); } finally { setVerifying(false); }
  };
  if (!d) return null;
  const verified = d.email_domain_status === "verified";
  const kindLabel = { gateway_error: "Gateway error", checkout_failed: "Checkout failed", signature_mismatch: "Signature mismatch", verify_error: "Verify error" };
  return (
    <div data-testid="payment-health-panel" className="mt-6 rounded-2xl border border-rose-400/20 bg-[#131C2E] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-rose-400" /> Payment health &amp; alerts</h3>
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span data-testid="alert-numbers" className="rounded-full bg-white/5 px-3 py-1 text-slate-400">WhatsApp alerts → {d.alert_numbers.length ? d.alert_numbers.join(", ") : "not set"}</span>
          <span className={`rounded-full px-3 py-1 flex items-center gap-1 ${d.email_configured ? "bg-emerald-400/10 text-emerald-300" : "bg-amber-400/10 text-amber-300"}`}>
            <MailCheck className="h-3 w-3" /> receipts {d.email_configured ? "on" : "off"}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-xl bg-white/[0.03] p-4"><p className="font-display text-2xl font-extrabold text-emerald-300" data-testid="paid-24h">{d.paid_24h}</p><p className="text-xs text-slate-400">paid (24h)</p></div>
        <div className="rounded-xl bg-white/[0.03] p-4"><p className={`font-display text-2xl font-extrabold ${d.failures_24h ? "text-rose-300" : "text-white"}`} data-testid="failures-24h">{d.failures_24h}</p><p className="text-xs text-slate-400">failures (24h)</p></div>
        <div className="rounded-xl bg-white/[0.03] p-4"><p className="font-display text-2xl font-extrabold text-cyan-300" data-testid="receipts-sent">{d.receipts_sent}</p><p className="text-xs text-slate-400">receipts emailed</p></div>
      </div>
      <div className="mt-4 space-y-1.5" data-testid="payment-failure-log">
        {d.failures.length === 0 && <p className="text-sm text-slate-500">No payment failures recorded. 🎉</p>}
        {d.failures.map((f, i) => (
          <div key={i} className="flex flex-wrap items-center justify-between gap-2 text-xs border-b border-white/5 py-1.5">
            <span className="rounded-full bg-rose-400/10 px-2 py-0.5 text-rose-300">{kindLabel[f.kind] || f.kind}</span>
            <span className="text-slate-300 flex-1 min-w-[160px] truncate" title={f.detail}>{f.detail}</span>
            <span className="text-slate-500">{f.user_email || "-"}</span>
            <span className="text-slate-500">{f.plan || "-"}</span>
            <span className={f.alerted ? "text-emerald-400" : "text-slate-600"}>{f.alerted ? "alerted" : "muted"}</span>
            <span className="text-slate-500">{new Date(f.created_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-slate-500">Alerts fire on gateway errors, failed customer payments and signature mismatches; repeats of the same kind are muted for 15 minutes.</p>

      <div className="mt-5 border-t border-white/5 pt-4" data-testid="email-domain-section">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium text-slate-300 flex items-center gap-2">
            <MailCheck className="h-4 w-4 text-cyan-400" /> Email sender: <span data-testid="email-sender" className="font-mono-data text-xs text-slate-200">{d.email_sender}</span>
            <span data-testid="email-domain-status" className={`rounded-full px-2 py-0.5 text-[11px] ${verified ? "bg-emerald-400/10 text-emerald-300" : "bg-amber-400/10 text-amber-300"}`}>
              {d.email_domain} · {d.email_domain_status || "unknown"}
            </span>
          </p>
          <div className="flex gap-2">
            {!verified && <button data-testid="show-dns-btn" onClick={() => setShowDns((s) => !s)} className="rounded-full bg-white/5 px-3 py-1.5 text-xs text-slate-300">{showDns ? "Hide" : "Show"} DNS records</button>}
            <button data-testid="verify-domain-btn" onClick={verifyDomain} disabled={verifying}
              className="flex items-center gap-1.5 rounded-full bg-cyan-400 px-3 py-1.5 text-xs font-bold text-[#041016] disabled:opacity-50">
              {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} {verified ? "Re-check" : "Verify DNS now"}
            </button>
          </div>
        </div>
        {!verified && <p className="mt-1.5 text-[11px] text-amber-300/80">Until the domain is verified, receipts and refund emails only reach the Resend account owner's inbox. Add the DNS records below at your domain registrar, then click Verify.</p>}
        {showDns && !verified && (
          <div className="mt-3 overflow-x-auto" data-testid="dns-records">
            <table className="w-full text-[11px]">
              <thead><tr className="text-slate-500 text-left"><th className="py-1 pr-3">Type</th><th className="py-1 pr-3">Host / Name</th><th className="py-1 pr-3">Value</th><th className="py-1">Status</th></tr></thead>
              <tbody>
                {d.email_dns_records.map((r, i) => (
                  <tr key={i} className="border-t border-white/5 align-top">
                    <td className="py-1.5 pr-3 font-mono-data text-slate-300">{r.type}{r.priority ? ` (prio ${r.priority})` : ""}</td>
                    <td className="py-1.5 pr-3 font-mono-data text-cyan-300">{r.name}</td>
                    <td className="py-1.5 pr-3 font-mono-data text-slate-400 break-all max-w-md">{r.value}</td>
                    <td className="py-1.5 text-slate-500">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function OrdersPanel() {
  const [orders, setOrders] = useState(null);
  const [confirmId, setConfirmId] = useState("");
  const [reason, setReason] = useState("Requested by customer");
  const [busy, setBusy] = useState("");
  const load = () => { api.get("/admin/orders?limit=15").then((r) => setOrders(r.data.orders || [])).catch(() => setOrders([])); };
  useEffect(load, []);
  const refund = async (o) => {
    setBusy(o.order_id);
    try {
      const { data } = await api.post(`/admin/orders/${o.order_id}/refund`, { reason });
      toast.success(`Refunded ₹${data.amount} to ${o.user_email} (${data.refund_id})`);
      setConfirmId("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setBusy("");
    }
  };
  if (!orders) return null;
  return (
    <div data-testid="orders-panel" className="mt-6 rounded-2xl border border-indigo-400/20 bg-[#131C2E] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Banknote className="h-5 w-5 text-indigo-400" /> Paid orders &amp; refunds</h3>
        <span className="rounded-full bg-white/5 px-3 py-1 text-[11px] text-slate-400">Refunds go back to the customer's original payment method via Razorpay (5-7 working days)</span>
      </div>
      {orders.length === 0 && <p className="text-sm text-slate-500">No paid orders yet.</p>}
      <div className="space-y-1.5">
        {orders.map((o) => (
          <div key={o.order_id} data-testid={`order-row-${o.order_id}`} className="flex flex-wrap items-center justify-between gap-2 text-xs border-b border-white/5 py-2">
            <div className="min-w-[180px]">
              <p className="text-slate-200">{o.user_email || "-"}</p>
              <p className="font-mono-data text-[10px] text-slate-500">{o.payment_id}</p>
            </div>
            <span className="text-slate-300 capitalize">{o.plan}</span>
            <span className="text-slate-200 font-mono-data">₹{o.amount}{o.coupon ? <span className="text-emerald-400"> ({o.coupon})</span> : null}</span>
            <span className="text-slate-500">{new Date(o.created_at).toLocaleDateString()}</span>
            {o.status === "refunded" ? (
              <span className="rounded-full bg-rose-400/10 px-2 py-0.5 text-rose-300" title={`by ${o.refund?.by} · ${o.refund?.reason}`}>refunded ₹{o.refund?.amount}</span>
            ) : confirmId === o.order_id ? (
              <div className="flex items-center gap-1.5">
                <input data-testid={`refund-reason-${o.order_id}`} value={reason} onChange={(e) => setReason(e.target.value)}
                  className="w-40 rounded-md border border-white/10 bg-[#0B1420] px-2 py-1 text-xs text-white outline-none" />
                <button data-testid={`refund-confirm-${o.order_id}`} onClick={() => refund(o)} disabled={busy === o.order_id}
                  className="flex items-center gap-1 rounded-md bg-rose-500 px-2.5 py-1 font-bold text-white disabled:opacity-50">
                  {busy === o.order_id ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Confirm ₹{o.amount}
                </button>
                <button data-testid={`refund-cancel-${o.order_id}`} onClick={() => setConfirmId("")} className="rounded-md bg-white/5 px-2 py-1 text-slate-300">Cancel</button>
              </div>
            ) : (
              <button data-testid={`refund-btn-${o.order_id}`} onClick={() => setConfirmId(o.order_id)}
                className="rounded-md border border-rose-400/30 px-2.5 py-1 text-rose-300 hover:bg-rose-400/10">Refund</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CouponPanel() {
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [granting, setGranting] = useState(false);
  const [grants, setGrants] = useState([]);
  const loadGrants = () => api.get("/admin/offer-grants?limit=8").then((r) => setGrants(r.data.grants || [])).catch(() => {});
  useEffect(() => { api.get("/admin/coupon-analytics").then((r) => setD(r.data)).catch(() => {}); loadGrants(); }, []);
  const sendDigest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/recovery-report/send");
      const g = data.digest;
      toast.success(`Recovery report sent — ${g.carts_recovered} recovered, ₹${g.revenue_recovered} recovered`);
    } catch {
      toast.error("Could not send report");
    } finally {
      setSending(false);
    }
  };
  const grantOffer = async () => {
    if (!grantEmail.trim()) return;
    setGranting(true);
    try {
      const { data } = await api.post("/admin/issue-offer", { email: grantEmail.trim(), code: "COMEBACK200" });
      const when = new Date(data.expires_at).toLocaleString();
      toast.success(`${data.label} issued to ${data.email} — valid until ${when}`);
      setGrantEmail("");
      loadGrants();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not issue offer");
    } finally {
      setGranting(false);
    }
  };
  if (!d) return null;
  return (
    <div data-testid="coupon-panel" className="mt-6 rounded-2xl border border-emerald-400/20 bg-[#131C2E] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Ticket className="h-5 w-5 text-emerald-400" /> Win-back &amp; coupon recovery</h3>
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-[11px] text-emerald-300">recovery rate: {d.recovery_rate}%</span>
          <button data-testid="send-recovery-report-btn" onClick={sendDigest} disabled={sending}
            className="flex items-center gap-1.5 rounded-full bg-emerald-400 px-3 py-1.5 text-xs font-bold text-[#041016] disabled:opacity-50">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send report now
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="rounded-xl bg-white/[0.03] p-4"><p className="font-display text-2xl font-extrabold text-cyan-300" data-testid="coupon-carts-nudged">{d.carts_nudged}</p><p className="text-xs text-slate-400">carts nudged</p></div>
        <div className="rounded-xl bg-white/[0.03] p-4"><p className="font-display text-2xl font-extrabold text-indigo-300">{d.carts_nudged_stage2}</p><p className="text-xs text-slate-400">2nd-nudge sent</p></div>
        <div className="rounded-xl bg-white/[0.03] p-4"><p className="font-display text-2xl font-extrabold text-amber-300" data-testid="coupon-carts-recovered">{d.carts_recovered}</p><p className="text-xs text-slate-400">carts recovered</p></div>
        <div className="rounded-xl bg-white/[0.03] p-4"><p className="font-display text-2xl font-extrabold text-emerald-300" data-testid="coupon-revenue-recovered">₹{d.revenue_recovered}</p><p className="text-xs text-slate-400">revenue recovered</p></div>
        <div className="rounded-xl bg-white/[0.03] p-4"><p className="font-display text-2xl font-extrabold text-white" data-testid="coupon-redeemed">{d.coupons_redeemed}</p><p className="text-xs text-slate-400">coupons redeemed</p></div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-400">
        <span>Coupons applied at checkout: <b className="text-slate-200">{d.coupons_applied}</b></span>
        <span>Total discount given: <b className="text-slate-200">₹{d.discount_given}</b></span>
      </div>
      {d.by_code.length > 0 && (
        <div className="mt-4 space-y-1.5">
          {d.by_code.map((c) => (
            <div key={c.code} className="flex items-center justify-between text-sm border-b border-white/5 py-1.5">
              <span className="font-mono-data text-xs text-emerald-300">{c.code}</span>
              <span className="text-slate-400">{c.redeemed} redeemed</span>
              <span className="text-slate-300 font-mono-data text-xs">₹{c.discount} off</span>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-[11px] text-slate-500">Recovery = nudged carts that later converted to a paid plan.</p>

      <div className="mt-5 border-t border-white/5 pt-4">
        <p className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2"><Gift className="h-4 w-4 text-amber-400" /> Grant a 48h win-back offer manually</p>
        <div className="flex flex-col sm:flex-row gap-2 max-w-md">
          <input data-testid="grant-offer-email" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)}
            placeholder="student@email.com" type="email"
            className="flex-1 rounded-lg border border-white/10 bg-[#0B1420] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-amber-400/50 outline-none" />
          <button data-testid="grant-offer-btn" onClick={grantOffer} disabled={granting || !grantEmail.trim()}
            className="flex items-center justify-center gap-1.5 rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-[#041016] disabled:opacity-50">
            {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />} Issue COMEBACK200
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-slate-500">Use when a customer asks for the discount but the WhatsApp/SMS nudge didn't reach them.</p>
        {grants.length > 0 && (
          <div className="mt-4" data-testid="grant-audit-log">
            <p className="text-xs font-medium text-slate-400 mb-2">Recent manual grants</p>
            <div className="space-y-1.5">
              {grants.map((g, i) => (
                <div key={i} className="flex flex-wrap items-center justify-between gap-2 text-xs border-b border-white/5 py-1.5">
                  <span className="text-slate-200">{g.target_email}</span>
                  <span className="font-mono-data text-emerald-300">{g.code}</span>
                  <span className="text-slate-500">by {g.granted_by_email}</span>
                  <span className="text-slate-500">{new Date(g.granted_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



const cmap = {
  cyan: "text-cyan-300 border-cyan-400/20 bg-cyan-400/5",
  indigo: "text-indigo-300 border-indigo-400/20 bg-indigo-400/5",
  emerald: "text-emerald-300 border-emerald-400/20 bg-emerald-400/5",
  amber: "text-amber-300 border-amber-400/20 bg-amber-400/5",
};
const Metric = ({ icon: Icon, label, value, color }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="admin-metric-card"
    className="rounded-2xl border border-white/8 bg-gradient-to-br from-[#131C2E] to-[#0F1626] p-5">
    <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl border ${cmap[color]}`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className="font-display text-2xl font-extrabold text-white">{value}</p>
    <p className="text-xs text-slate-400 mt-0.5">{label}</p>
  </motion.div>
);
